import {
  type Activities_Insert_Input,
  Activity_Types_Enum_Enum,
  type Lots_Set_Input,
} from '@graphql/__generated__/graphql';
import {
  ChangeExtraDataSchema,
  type DeactivationExtraData,
  type LotStatus,
  type PlainStatusChangeExtraData,
  type PriceField,
  type RejectionExtraData,
  type RejectionFsbo,
  parseLotStatus,
} from '@modules/_shared/utils/lotChangeExtraData';

/**
 * Rules that map a lot field change to a `change` activity row.
 *
 * Mirror of:
 *  - agg-next/src/lib/lot-change-rules.ts
 *  - realadvisor-app/src/lib/lot-change-rules.ts
 *
 * All three repos compute the same `change` activity rows from a lot diff —
 * keep them in sync whenever one of them changes.
 *
 * The frontend computes activities by diffing the current lot snapshot against
 * the `lots_set_input` payload it's about to write, then sends both the lot
 * update and the activity inserts in a single Hasura multi-mutation request
 * (one Postgres transaction). See UPDATE_LISTING_TRACKED in lotsQueries.ts.
 *
 * extra_data shape is enforced by ChangeExtraDataSchema (see lotChangeExtraData.ts);
 * each row's extra_data is parsed before insertion so a malformed shape from a
 * future caller throws here rather than landing in the DB.
 */

export type LotChangeContext = {
  /** Rejection-only context (status: 'rejected'). */
  rejection?: {
    reason_code: RejectionFsbo['reason_code'];
    seller_message?: string;
    internal_note?: string;
  };
};

export type LotSnapshot = {
  status?: LotStatus | null;
  offer_type?: string | null;
  sale_price?: number | null;
  rent_net?: number | null;
  rent_extra?: number | null;
};

export type BuildActivityCtx = {
  lotId: string;
  tenantId?: string | null;
  sessionUserId?: string | null;
  /** Lot currency at write-time, embedded in `extra_data.price` so the feed
   *  can format the value without re-querying the lot (and so historical
   *  activities preserve the currency they were written with). */
  currency?: string | null;
  changeContext?: LotChangeContext;
};

type ActivityFields = Omit<
  Activities_Insert_Input,
  'lot_id' | 'activity_type' | 'tenant_id' | 'created_by'
>;

export type LotChangeRule = {
  field: keyof LotSnapshot;
  applies: (old: LotSnapshot, set: Lots_Set_Input) => boolean;
  buildActivity: (
    old: LotSnapshot,
    set: Lots_Set_Input,
    ctx: BuildActivityCtx,
  ) => ActivityFields;
};

// Activities of type `change` carry no `subject` — the feed builds the title
// from `extra_data` (see FeedItem.tsx). `subject` is reserved for email/note
// style activities.
export const lotChangeRules: LotChangeRule[] = [
  {
    field: 'status',
    applies: (old, set) =>
      set.status !== undefined && set.status !== old.status,
    // Status changes split into three shapes:
    //   - paused   → DeactivationExtraData
    //   - rejected → RejectionExtraData (CRM staff)
    //   - other    → PlainStatusChangeExtraData
    buildActivity: (old, set, { changeContext }) => {
      const from = old.status ?? null;
      const to = set.status as LotStatus;
      const baseFields = { done: true, done_at: 'now()', success: true };
      if (to === 'rejected') {
        const rejection = changeContext?.rejection;
        const extra: RejectionExtraData = {
          status: { from, to: 'rejected' },
          ...(rejection?.reason_code
            ? {
                fsbo: {
                  reason_code: rejection.reason_code,
                  ...(rejection.seller_message
                    ? { seller_message: rejection.seller_message }
                    : {}),
                },
              }
            : {}),
        };
        return {
          ...baseFields,
          success: false,
          note: rejection?.internal_note ?? null,
          extra_data: extra,
        };
      }
      if (to === 'paused') {
        // CRM staff don't deactivate listings — that's an FSBO-side flow.
        // We still emit a typed Deactivation row if it ever happens.
        const extra: DeactivationExtraData = {
          status: { from, to: 'paused' },
        };
        return { ...baseFields, extra_data: extra };
      }
      const extra: PlainStatusChangeExtraData = {
        status: { from, to: to as PlainStatusChangeExtraData['status']['to'] },
      };
      return { ...baseFields, extra_data: extra };
    },
  },
  {
    field: 'offer_type',
    applies: (old, set) =>
      set.offer_type !== undefined &&
      set.offer_type !== old.offer_type &&
      // Only log when there's an actual previous value — first-time set
      // happens during listing creation and shouldn't appear in the feed.
      old.offer_type != null,
    buildActivity: (old, set) => ({
      done: true,
      done_at: 'now()',
      success: true,
      extra_data: {
        offer_type: { from: old.offer_type ?? null, to: set.offer_type! },
      },
    }),
  },
  {
    field: 'sale_price',
    // Skip null-crossing transitions (sale_price becoming null when the
    // offer_type switches to rent, or vice versa) — those are side-effects
    // of an offer_type change, not deliberate price edits. The offer_type
    // rule above captures the meaningful event.
    applies: (old, set) =>
      set.sale_price !== undefined &&
      set.sale_price !== old.sale_price &&
      set.sale_price != null &&
      old.sale_price != null,
    buildActivity: (old, set, ctx) => ({
      done: true,
      done_at: 'now()',
      success: true,
      extra_data: {
        price: {
          field: 'sale_price' satisfies PriceField,
          from: old.sale_price!,
          to: set.sale_price!,
          // Prefer the new currency from `set` when the form is
          // changing currency in the same submit; otherwise fall back
          // to the pre-write currency the caller passed in ctx.
          currency: set.currency ?? ctx.currency ?? null,
        },
      },
    }),
  },
  {
    field: 'rent_net',
    applies: (old, set) =>
      set.rent_net !== undefined &&
      set.rent_net !== old.rent_net &&
      set.rent_net != null &&
      old.rent_net != null,
    buildActivity: (old, set, ctx) => ({
      done: true,
      done_at: 'now()',
      success: true,
      extra_data: {
        price: {
          field: 'rent_net' satisfies PriceField,
          from: old.rent_net!,
          to: set.rent_net!,
          // Prefer the new currency from `set` when the form is
          // changing currency in the same submit; otherwise fall back
          // to the pre-write currency the caller passed in ctx.
          currency: set.currency ?? ctx.currency ?? null,
        },
      },
    }),
  },
  {
    field: 'rent_extra',
    applies: (old, set) =>
      set.rent_extra !== undefined &&
      set.rent_extra !== old.rent_extra &&
      set.rent_extra != null &&
      old.rent_extra != null,
    buildActivity: (old, set, ctx) => ({
      done: true,
      done_at: 'now()',
      success: true,
      extra_data: {
        price: {
          field: 'rent_extra' satisfies PriceField,
          from: old.rent_extra!,
          to: set.rent_extra!,
          // Prefer the new currency from `set` when the form is
          // changing currency in the same submit; otherwise fall back
          // to the pre-write currency the caller passed in ctx.
          currency: set.currency ?? ctx.currency ?? null,
        },
      },
    }),
  },
];

/**
 * Diffs `set` against the lot's current values and returns the activity rows
 * to insert. Returns [] when no tracked field changed. Each row's extra_data
 * is safe-parsed against ChangeExtraDataSchema; a malformed shape drops the
 * single offending row and logs, so a bad activity payload can't block the
 * surrounding listing update.
 */
export const buildLotChangeActivities = (
  old: LotSnapshot,
  set: Lots_Set_Input,
  ctx: BuildActivityCtx,
): Activities_Insert_Input[] => {
  // Narrow the snapshot's status at the boundary so the rule can rely on it.
  const safeOld: LotSnapshot = {
    ...old,
    status: parseLotStatus(old.status),
  };
  return lotChangeRules
    .filter(rule => rule.field in set && rule.applies(safeOld, set))
    .flatMap(rule => {
      const fields = rule.buildActivity(safeOld, set, ctx);
      const parsed = ChangeExtraDataSchema.safeParse(fields.extra_data);
      if (!parsed.success) {
        console.error(
          `[lotChangeRules] dropping ${rule.field} change activity — extra_data failed schema validation`,
          parsed.error,
        );
        return [];
      }
      return [
        {
          activity_type: Activity_Types_Enum_Enum.Change,
          lot_id: ctx.lotId,
          tenant_id: ctx.tenantId ?? undefined,
          created_by: ctx.sessionUserId ?? undefined,
          // The Relay feed filter (resolveList.js) treats activity_type='change'
          // rows with NULL visibility as private (NOT predicate evaluates to NULL
          // and SQL drops NULL-result rows), so set 'shared' explicitly here.
          visibility: 'shared',
          ...fields,
        },
      ];
    });
};
